import { useState, useCallback } from "react";
import { Football, Scales, MagnifyingGlass, Plus, X, ArrowRight, GoogleLogo, Lightning, Check, Copy } from "@phosphor-icons/react";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link } from "react-router-dom";
import axios from "axios";
import { trackEvent, API } from "@/App";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import PublicNav from "@/components/PublicNav";
import { PlayerPhoto } from "@/components/PlayerPhoto";

function PosBadge({ pos }) {
  const cls = `pos-bg-${(pos || "").toLowerCase()} pos-${(pos || "").toLowerCase()}`;
  return <span className={`inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${cls}`}>{pos}</span>;
}

export default function PublicCalculator() {
  const [sideA, setSideA] = useState([]);
  const [sideB, setSideB] = useState([]);
  const [searchA, setSearchA] = useState("");
  const [searchB, setSearchB] = useState("");
  const [resultsA, setResultsA] = useState([]);
  const [resultsB, setResultsB] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const searchPlayers = useCallback(async (q, side) => {
    if (q.length < 2) { side === "a" ? setResultsA([]) : setResultsB([]); return; }
    try {
      const res = await axios.get(`${API}/players/search?q=${encodeURIComponent(q)}&numQbs=2&numTeams=12&ppr=1`);
      side === "a" ? setResultsA(res.data || []) : setResultsB(res.data || []);
    } catch (e) { /* silent */ }
  }, []);

  const addPlayer = (player, side) => {
    const item = { name: player.name, position: player.position, value: player.value, sleeperId: player.sleeperId };
    if (side === "a") { setSideA(prev => [...prev, item]); setSearchA(""); setResultsA([]); }
    else { setSideB(prev => [...prev, item]); setSearchB(""); setResultsB([]); }
    setResult(null);
  };

  const removePlayer = (idx, side) => {
    if (side === "a") setSideA(prev => prev.filter((_, i) => i !== idx));
    else setSideB(prev => prev.filter((_, i) => i !== idx));
    setResult(null);
  };

  const evaluate = async () => {
    if (sideA.length === 0 || sideB.length === 0) return;
    setLoading(true);
    trackEvent("feature_used", "public_calculator");
    try {
      const res = await axios.post(`${API}/trade/evaluate`, {
        side_a: sideA.map(p => p.name),
        side_b: sideB.map(p => p.name),
        league_settings: { num_qbs: 2, num_teams: 12, ppr: 1 },
      });
      setResult(res.data);
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  const totalA = sideA.reduce((s, p) => s + (p.value || 0), 0);
  const totalB = sideB.reduce((s, p) => s + (p.value || 0), 0);
  const verdictColors = { FAIR: "#4ade80", CLOSE: "#fbbf24", LOPSIDED: "#ff3b30" };

  const handleGoogleLogin = () => {
    const r = window.location.origin + '/';
    window.location.href = '/api/auth/google/login';
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO title="Free Dynasty Trade Calculator — Evaluate Any Trade Instantly | Dynasty Trade Maker" description="The dynasty trade calculator powered by real data from 6,976 championship leagues. Compare players and picks with daily-updated values. Superflex and 1QB. Free, no signup." path="/trade-calculator" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":[{"@type":"Question","name":"Is this dynasty trade calculator free?","acceptedAnswer":{"@type":"Answer","text":"Yes, completely free with no account required. Add players to both sides and get an instant verdict. For AI-powered trade suggestions, import your Sleeper league (also free)."}},{"@type":"Question","name":"How are dynasty trade values calculated?","acceptedAnswer":{"@type":"Answer","text":"Values come from FantasyCalc consensus dynasty rankings, aggregating data from 150,000+ real Sleeper leagues. Updated daily."}},{"@type":"Question","name":"What's the difference between Superflex and 1QB values?","acceptedAnswer":{"@type":"Answer","text":"In Superflex, you start two QBs, making the position dramatically more valuable. A QB worth 5,000 in 1QB might be 10,000+ in Superflex."}},{"@type":"Question","name":"Should I always take the side with more value?","acceptedAnswer":{"@type":"Answer","text":"Not necessarily. Research on 27,000+ lopsided trades shows the side that loses on value wins championships more often (10.0% vs 6.7%). Context and roster fit matter."}}]})}</script>
      </Helmet>
      
      {/* Header */}
      <PublicNav />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight text-white uppercase mb-2" data-testid="public-calc-title">
            TRADE <span className="text-[#ccff00]">CALCULATOR</span>
          </h1>
          <p className="text-sm text-[#a3a3a3]">Search any player or draft pick. Instantly see if a trade is fair. No account needed.</p>
        </div>

        {/* Result */}
        {result && (
          <div className="border border-[#262626] bg-[#171717] p-5 mb-6 text-center" data-testid="calc-result">
            <span className="text-3xl font-bold px-4 py-1 rounded-sm" style={{ color: verdictColors[result.verdict] || '#fff', backgroundColor: `${verdictColors[result.verdict] || '#fff'}15` }}>
              {result.verdict}
            </span>
            <p className="text-sm text-[#a3a3a3] mt-2">{result.difference_pct}% difference {result.favors !== "EVEN" ? `— favors ${result.favors}` : ""}</p>
            {result.waiver_explanation && <p className="text-[10px] text-[#3b82f6] mt-1">{result.waiver_explanation}</p>}
            <button onClick={() => {
              const aStr = sideA.map(p => p.name).join(" + ");
              const bStr = sideB.map(p => p.name).join(" + ");
              navigator.clipboard.writeText(`Trade check:\n${aStr}\nfor\n${bStr}\n\nVerdict: ${result.verdict} (${result.difference_pct}%)\n\nCheck yours at dynastytrademaker.com/trade-calculator`);
              toast.success("Copied — share it with your league!");
            }} className="mt-3 inline-flex items-center gap-1 px-4 py-1.5 border border-[#262626] text-[#a3a3a3] hover:border-[#ccff00] hover:text-[#ccff00] rounded-sm text-[10px] font-bold uppercase">
              <Copy size={12} weight="bold" /> COPY RESULT
            </button>
          </div>
        )}

        {/* Two Sides */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Side A */}
          <div className="border border-[#262626] bg-[#171717] p-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#ff3b30] mb-3">SIDE A {totalA > 0 && <span className="font-mono text-[#a3a3a3] ml-2">{totalA.toLocaleString()}</span>}</p>
            <div className="relative mb-3">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
              <Input placeholder="Search player or pick..." value={searchA} onChange={(e) => { setSearchA(e.target.value); searchPlayers(e.target.value, "a"); }} className="h-10 pl-8 bg-[#0a0a0a] border-[#262626] text-white text-sm placeholder:text-[#444]" data-testid="search-a" />
              {resultsA.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#171717] border border-[#262626] z-50 max-h-[200px] overflow-auto">
                  {resultsA.slice(0, 8).map(p => (
                    <button key={p.id} onClick={() => addPlayer(p, "a")} className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#262626] text-left">
                      <div className="flex items-center gap-2"><PlayerPhoto playerId={p.sleeperId} name={p.name} position={p.position} size={24} /><span className="text-sm text-white">{p.name}</span></div>
                      <span className="font-mono text-xs text-[#a3a3a3]">{p.value?.toLocaleString()}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5 min-h-[60px]">
              {sideA.map((p, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 bg-[#0a0a0a] border border-[#262626]">
                  <div className="flex items-center gap-2"><PlayerPhoto playerId={p.sleeperId} name={p.name} position={p.position} size={24} /><span className="text-sm text-white">{p.name}</span></div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-[#a3a3a3]">{(p.value || 0).toLocaleString()}</span>
                    <button onClick={() => removePlayer(i, "a")} className="text-[#666] hover:text-[#ff3b30]"><X size={12} /></button>
                  </div>
                </div>
              ))}
              {sideA.length === 0 && <p className="text-xs text-[#666] text-center py-4">Search and add players above</p>}
            </div>
          </div>

          {/* Side B */}
          <div className="border border-[#262626] bg-[#171717] p-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#4ade80] mb-3">SIDE B {totalB > 0 && <span className="font-mono text-[#a3a3a3] ml-2">{totalB.toLocaleString()}</span>}</p>
            <div className="relative mb-3">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#666]" />
              <Input placeholder="Search player or pick..." value={searchB} onChange={(e) => { setSearchB(e.target.value); searchPlayers(e.target.value, "b"); }} className="h-10 pl-8 bg-[#0a0a0a] border-[#262626] text-white text-sm placeholder:text-[#444]" data-testid="search-b" />
              {resultsB.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#171717] border border-[#262626] z-50 max-h-[200px] overflow-auto">
                  {resultsB.slice(0, 8).map(p => (
                    <button key={p.id} onClick={() => addPlayer(p, "b")} className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#262626] text-left">
                      <div className="flex items-center gap-2"><PlayerPhoto playerId={p.sleeperId} name={p.name} position={p.position} size={24} /><span className="text-sm text-white">{p.name}</span></div>
                      <span className="font-mono text-xs text-[#a3a3a3]">{p.value?.toLocaleString()}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-1.5 min-h-[60px]">
              {sideB.map((p, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 bg-[#0a0a0a] border border-[#262626]">
                  <div className="flex items-center gap-2"><PlayerPhoto playerId={p.sleeperId} name={p.name} position={p.position} size={24} /><span className="text-sm text-white">{p.name}</span></div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-[#a3a3a3]">{(p.value || 0).toLocaleString()}</span>
                    <button onClick={() => removePlayer(i, "b")} className="text-[#666] hover:text-[#ff3b30]"><X size={12} /></button>
                  </div>
                </div>
              ))}
              {sideB.length === 0 && <p className="text-xs text-[#666] text-center py-4">Search and add players above</p>}
            </div>
          </div>
        </div>

        <Button onClick={evaluate} disabled={loading || sideA.length === 0 || sideB.length === 0} className="w-full h-12 btn-primary rounded-sm text-sm mb-8" data-testid="evaluate-btn">
          {loading ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <><Scales size={18} weight="bold" className="mr-2" />EVALUATE TRADE</>}
        </Button>

        {/* Upsell */}
        <div className="border border-[#ccff00]/30 bg-[#ccff00]/5 p-6 text-center mb-8">
          <h3 className="font-heading text-xl text-white uppercase mb-2">WANT AI-POWERED TRADE SUGGESTIONS?</h3>
          <p className="text-sm text-[#a3a3a3] mb-4">Import your Sleeper league and get 6 personalized trade packages built for YOUR roster. 30 free credits, no signup required.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button onClick={() => { trackEvent("try_free_click"); window.location.href = "/"; }} className="h-10 px-6 btn-primary rounded-sm text-xs"><Lightning size={14} weight="bold" className="mr-1" />IMPORT YOUR LEAGUE</Button>
            <Link to="/features/trade-analyzer" className="text-xs text-[#ccff00] no-underline hover:underline flex items-center gap-1">See how it works <ArrowRight size={12} /></Link>
          </div>
        </div>

        {/* Context Strip: Popular trades & comparisons */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-2">POPULAR COMPARISONS</h3>
            <div className="space-y-1.5">
              {[
                ["Bijan Robinson", "Jahmyr Gibbs"],
                ["Josh Allen", "Lamar Jackson"],
                ["Ja'Marr Chase", "Justin Jefferson"],
                ["Puka Nacua", "Malik Nabers"],
              ].map(([a, b], i) => {
                const slugA = a.toLowerCase().replace(/[.']/g,"").replace(/\s+/g,"-");
                const slugB = b.toLowerCase().replace(/[.']/g,"").replace(/\s+/g,"-");
                const [s1,s2] = [slugA,slugB].sort();
                return (
                  <Link key={i} to={`/compare/${s1}-vs-${s2}`} className="flex items-center justify-between px-3 py-2 border border-[#262626] bg-[#171717] hover:border-[#444] no-underline rounded-sm transition-colors">
                    <span className="text-xs text-[#a3a3a3]">{a.split(" ").pop()} vs {b.split(" ").pop()}</span>
                    <ArrowRight size={10} className="text-[#555]" />
                  </Link>
                );
              })}
            </div>
          </div>
          <div>
            <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#555] mb-2">EXPLORE MORE</h3>
            <div className="space-y-1.5">
              <Link to="/trades" className="flex items-center justify-between px-3 py-2 border border-[#262626] bg-[#171717] hover:border-[#444] no-underline rounded-sm transition-colors">
                <span className="text-xs text-[#a3a3a3]">Real Trade Database</span>
                <span className="text-[9px] text-[#555]">40,000+ trades</span>
              </Link>
              <Link to="/rankings" className="flex items-center justify-between px-3 py-2 border border-[#262626] bg-[#171717] hover:border-[#444] no-underline rounded-sm transition-colors">
                <span className="text-xs text-[#a3a3a3]">Dynasty Value Chart</span>
                <span className="text-[9px] text-[#555]">Updated daily</span>
              </Link>
              <Link to="/research/championship-report" className="flex items-center justify-between px-3 py-2 border border-[#262626] bg-[#171717] hover:border-[#444] no-underline rounded-sm transition-colors">
                <span className="text-xs text-[#a3a3a3]">Championship Report</span>
                <span className="text-[9px] text-[#555]">6,976 champs analyzed</span>
              </Link>
              <Link to="/compare" className="flex items-center justify-between px-3 py-2 border border-[#262626] bg-[#171717] hover:border-[#444] no-underline rounded-sm transition-colors">
                <span className="text-xs text-[#a3a3a3]">Player Comparisons</span>
                <span className="text-[9px] text-[#555]">3,000+ head-to-heads</span>
              </Link>
            </div>
          </div>
        </div>

        {/* SEO Content */}
        <div className="mt-16 border-t border-[#262626] pt-12 space-y-10">
          <div>
            <h2 className="font-heading text-2xl text-white uppercase mb-3">THE DYNASTY TRADE CALCULATOR BACKED BY REAL DATA</h2>
            <p className="text-sm text-[#a3a3a3] leading-relaxed mb-3">{"Most dynasty trade calculators show you the same thing: two columns of numbers from a consensus value chart. You add up both sides, see which is bigger, and call it a day. The problem? That tells you almost nothing about whether a trade actually helps your team."}</p>
            <p className="text-sm text-[#a3a3a3] leading-relaxed mb-3">{"Dynasty Trade Maker\u2019s calculator is different. It\u2019s powered by real trade data from 6,976 completed Sleeper dynasty leagues \u2014 167,265 actual trades, not hypothetical values. When you evaluate a trade here, you\u2019re seeing what players are really worth based on what championship-winning teams actually paid for them."}</p>
            <p className="text-sm text-[#a3a3a3] leading-relaxed">{"Every value comes from FantasyCalc\u2019s consensus dynasty rankings, updated daily. We show both Superflex and 1QB values so you\u2019re always comparing in the right format for your league. And our trade verdict doesn\u2019t just say \u201CSide A wins\u201D \u2014 it tells you the gap percentage and whether the deal is close enough that roster fit should decide it."}</p>
          </div>

          <div>
            <h2 className="font-heading text-xl text-white uppercase mb-3">HOW TO USE A DYNASTY TRADE CALCULATOR</h2>
            <p className="text-sm text-[#a3a3a3] leading-relaxed mb-3">{"A trade calculator is a starting point, not a verdict. Here\u2019s how to use it effectively:"}</p>
            <div className="space-y-3">
              {[
                ["Check the value gap, not just who \u201Cwins.\u201D", "A 2% gap means the trade is essentially even \u2014 let roster needs decide. A 30% gap means someone is giving up real value."],
                ["Always check both formats.", "A player\u2019s value can shift dramatically between Superflex and 1QB. Make sure you\u2019re evaluating in the right context for your league."],
                ["Factor in what the calculator can\u2019t see.", "Your team\u2019s window, positional needs, and league dynamics matter as much as raw value. Our research shows that the \u201Cvalue loser\u201D in lopsided trades actually wins championships more often \u2014 because they\u2019re contenders making aggressive moves."],
                ["Use it before you offer, not after.", "Run the trade before you send it. Know the gap so you can negotiate from an informed position."],
              ].map(([title, desc], i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="text-[#ccff00] font-bold text-xs shrink-0 mt-0.5">0{i + 1}</span>
                  <p className="text-sm text-[#a3a3a3] leading-relaxed"><strong className="text-white">{title}</strong> {desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-xl text-white uppercase mb-3">WHAT MAKES THIS CALCULATOR DIFFERENT</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="border border-[#262626] bg-[#171717] p-4 rounded-sm">
                <p className="text-sm font-bold text-white mb-1">Real Trade Context</p>
                <p className="text-xs text-[#888] leading-relaxed">{"Every value is grounded in what players actually sell for in real Sleeper dynasty leagues \u2014 not what a panel of experts thinks they\u2019re worth."}</p>
              </div>
              <div className="border border-[#262626] bg-[#171717] p-4 rounded-sm">
                <p className="text-sm font-bold text-white mb-1">Updated Daily</p>
                <p className="text-xs text-[#888] leading-relaxed">{"Values update every day from FantasyCalc consensus data across 150,000+ real leagues. You\u2019re always seeing the current market, not last week\u2019s."}</p>
              </div>
              <div className="border border-[#262626] bg-[#171717] p-4 rounded-sm">
                <p className="text-sm font-bold text-white mb-1">Superflex & 1QB</p>
                <p className="text-xs text-[#888] leading-relaxed">{"Toggle between formats instantly. QB values shift dramatically in Superflex vs 1QB \u2014 always evaluate in your league\u2019s format."}</p>
              </div>
              <div className="border border-[#262626] bg-[#171717] p-4 rounded-sm">
                <p className="text-sm font-bold text-white mb-1">Includes Draft Picks</p>
                <p className="text-xs text-[#888] leading-relaxed">{"Add future draft picks to either side. Pick values are consensus-based so you can evaluate player-for-pick swaps accurately."}</p>
              </div>
            </div>
          </div>

          <div>
            <h2 className="font-heading text-xl text-white uppercase mb-3">DYNASTY TRADE VALUE CHART</h2>
            <p className="text-sm text-[#a3a3a3] leading-relaxed mb-3">{"Looking for the full dynasty trade value chart? Our "}<Link to="/rankings" className="text-[#ccff00] no-underline hover:underline">rankings page</Link>{" shows every player\u2019s dynasty value with 30-day trends, sortable by position and format. Use it alongside the calculator to find trade targets and identify value gaps in your league."}</p>
            <p className="text-sm text-[#a3a3a3] leading-relaxed">{"For deeper analysis, explore our "}<Link to="/research" className="text-[#ccff00] no-underline hover:underline">33 original research articles</Link>{" covering everything from trade timing (champions traded 68% more in playoff weeks) to which draft picks actually hit (the 1.01 hits 91.9% of the time, but mid-second rounders drop to single digits)."}</p>
          </div>

          <div>
            <h2 className="font-heading text-xl text-white uppercase mb-3">FREQUENTLY ASKED QUESTIONS</h2>
            <div className="space-y-4">
              {[
                ["Is this dynasty trade calculator free?", "Yes, completely free with no account required. Add players to both sides and get an instant verdict. For AI-powered trade suggestions personalized to your roster, import your Sleeper league (also free, with 3 AI analyses per month)."],
                ["How are dynasty trade values calculated?", "Values come from FantasyCalc\u2019s consensus dynasty rankings, which aggregate data from 150,000+ real Sleeper leagues. They update daily and reflect the real market \u2014 not one person\u2019s opinion."],
                ["What\u2019s the difference between Superflex and 1QB values?", "In Superflex leagues, you start two quarterbacks, which makes the position dramatically more scarce and valuable. A QB who\u2019s worth 5,000 in 1QB might be worth 10,000+ in Superflex. Always check the right format for your league."],
                ["Should I always take the side with more value?", "Not necessarily. Our research on 27,000+ lopsided trades shows the side that \u201Closes\u201D on value actually wins championships more often (10.0% vs 6.7%). Context, roster fit, and timing matter more than raw value."],
                ["Can I add draft picks to the trade?", "Yes. Future rookie picks (1st through 4th round, multiple seasons) can be added to either side. Pick values reflect consensus market pricing."],
              ].map(([q, a], i) => (
                <div key={i} className="border border-[#262626] bg-[#171717] p-4 rounded-sm">
                  <h3 className="text-sm font-bold text-white mb-1.5">{q}</h3>
                  <p className="text-xs text-[#888] leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center pt-4">
            <p className="text-sm text-[#a3a3a3] mb-4">{"Want more than a calculator? Import your Sleeper league for AI trade suggestions, contender rankings, and roster analysis."}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/rankings" className="text-xs text-[#ccff00] no-underline hover:underline">Full Value Chart →</Link>
              <Link to="/trades" className="text-xs text-[#ccff00] no-underline hover:underline">Real Trade Database →</Link>
              <Link to="/research/do-teams-that-trade-more-win" className="text-xs text-[#ccff00] no-underline hover:underline">Trading Research →</Link>
              <Link to="/guide" className="text-xs text-[#ccff00] no-underline hover:underline">Dynasty Guides →</Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-[10px] text-[#666] space-x-3">
          <a href="/features" className="hover:text-[#a3a3a3] no-underline">Features</a>
          <a href="/faq" className="hover:text-[#a3a3a3] no-underline">FAQ</a>
          <a href="/guide" className="hover:text-[#a3a3a3] no-underline">Guide</a>
          <a href="/terms" className="hover:text-[#a3a3a3] no-underline">Terms</a>
          <span>dynastytrademaker.com</span>
        </div>
      </div>
    </div>
  );
}
